import * as React from 'react';

import styles from './PageIntro.module.scss';

export type PageIntroPoint = {
  /** Short lead-in, rendered bold. e.g. "Findable" */
  label: string;
  /** The rest of the sentence. */
  text: React.ReactNode;
};

interface PageIntroProps {
  /**
   * One paragraph: what this page's thing IS and why it is worth having.
   * Plain language, no feature-listing — the points below do that.
   */
  overview: React.ReactNode;
  /** Optional "what you get" list. Omit for pages that need only the paragraph. */
  points?: PageIntroPoint[];
  /** Heading above the points. Defaults to "What you get". */
  pointsHeading?: string;
  /** Optional footnote under the block — caveats, pricing, links. */
  footnote?: React.ReactNode;
  /**
   * Indent to line up with the page title.
   *
   * Every page in the app styles its `<PageTitle>` with `padding: 1.25rem`,
   * which a SIBLING does not inherit — so an intro dropped in after the title
   * hangs 20px to its left, and the misalignment is obvious once you see it.
   * Pass `inset` when this sits directly under `<PageTitle>`; omit it when the
   * intro is already inside a padded container (Asset Schemas, Share Presets),
   * where it would double up.
   */
  inset?: boolean;
}

/**
 * The standard page opener: one paragraph of business value, optionally
 * followed by a short "what you get" list.
 *
 * Extracted from Analytics Integrations and Scheduled agents, which had grown the
 * same shape independently and read markedly better for it — a customer
 * landing on either page learns what the feature is FOR before being shown a
 * list of rows. Asset Schemas and Share Presets opened straight into a table,
 * so the only people who understood them were the people who already did.
 * Share Presets did have good copy, but it lived in the empty state, which
 * means it disappeared for everyone the moment it had taught them anything.
 *
 * Keep the overview to one paragraph and the points to four or fewer: this
 * sits above the content people actually came for, and an intro that has to be
 * scrolled past is worse than none.
 */
export const PageIntro: React.FC<PageIntroProps> = ({
  overview,
  points,
  pointsHeading = 'What you get',
  footnote,
  inset,
}) => (
  <div className={inset ? `${styles.pageIntro} ${styles.inset}` : styles.pageIntro}>
    <p className={styles.overview}>{overview}</p>

    {points && points.length > 0 && (
      <div className={styles.points}>
        <h3 className={styles.pointsHeading}>{pointsHeading}</h3>
        <ul className={styles.pointsList}>
          {points.map((point) => (
            <li key={point.label}>
              <strong>{point.label}</strong> — {point.text}
            </li>
          ))}
        </ul>
      </div>
    )}

    {footnote && <p className={styles.footnote}>{footnote}</p>}
  </div>
);

export default PageIntro;
